import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SectionHeading, FadeIn } from "@/components/laa/Sections";
import { ALL_BRANDS, FEATURED_BRANDS, ABOUT_IMAGE, LINKEDIN_URL, FASHION_DETECTIVE_URL } from "@/data/siteData";
import { Linkedin, Search, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/* ---------------- ABOUT ---------------- */

const CREDENTIALS = [
  ["Expert Witness", "Experienced trial expert witness — federal and state civil and criminal cases, representing private clients and government agencies"],
  ["Certification", "Appraisers Association of America — Certified Member, specialty in Costume, Textiles & Luxury Goods"],
  ["Standards", "USPAP — Uniform Standards of Professional Appraisal Practice, The Appraisal Foundation"],
  ["Experience", "The RealReal (Chief Authenticator), eBay, Sotheby's, TikTok Shop"],
  ["Education", "GIA, Christie's, Fordham Law School, London College of Fashion, NYU, FIT"],
  ["Speaking", "SXSW, Fashion Law Institute, Stern Business School, SMU, Federal Bar Association"],
  ["Service", "Costume Society of America, Couture Council at FIT, International Society of Appraisers"],
];

export const About = () => (
  <section id="about" data-testid="about-section" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)] bg-[color:var(--laa-ink-2)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <SectionHeading label="About" title="Graham Wetzbarger" />
          <FadeIn delay={0.08}>
            <p className="mt-6 text-sm sm:text-base leading-relaxed text-[rgba(244,239,230,0.7)]">
              Graham Wetzbarger is a world-renowned expert in personal luxury goods, specializing in the
              authentication and appraisal of collectible handbags, jewelry, and watches. Over the past 18 years,
              Graham has worked with some of the largest resale businesses, including The RealReal, eBay,
              Sotheby's, and TikTok Shop.
            </p>
            <p className="mt-4 text-sm sm:text-base leading-relaxed text-[rgba(244,239,230,0.7)]">
              His authentication expertise grew from his education in Fashion Design and Art History. His thirst
              for knowledge has taken him around the world, studying at the GIA, Christie's, Fordham Law School,
              London College of Fashion, NYU, and FIT. An experienced trial expert witness, Graham has served in
              federal and state civil and criminal cases, representing both private clients and government agencies.
            </p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="about-linkedin-button"
                variant="outline"
                className="mt-8 rounded-xl bg-transparent border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] hover:bg-[rgba(244,239,230,0.05)] hover:text-[color:var(--laa-ivory)] hover:border-[rgba(244,239,230,0.3)]"
              >
                <Linkedin size={16} className="mr-2 text-[color:var(--laa-champagne)]" />
                View Profile on LinkedIn
              </Button>
            </a>
          </FadeIn>
        </div>
        <FadeIn delay={0.1} className="lg:col-span-5">
          <div className="relative noise rounded-2xl overflow-hidden border border-[color:var(--laa-hairline)] mb-6">
            <img src={ABOUT_IMAGE} alt="Graham Wetzbarger, founder of Luxury Appraisals & Authentication, in his New York office" className="w-full h-64 sm:h-72 object-cover object-[50%_20%]" loading="lazy" />
          </div>
          <div className="rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] divide-y divide-[color:var(--laa-hairline)]">
            {CREDENTIALS.map(([label, value]) => (
              <div key={label} className="p-4 sm:px-6">
                <p className="laa-label">{label}</p>
                <p className="mt-1.5 text-xs sm:text-sm leading-relaxed text-[rgba(244,239,230,0.72)]">{value}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ---------------- BRANDS ---------------- */

export const BrandsShowcase = () => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_BRANDS;
    return ALL_BRANDS.filter((b) => b.toLowerCase().includes(q));
  }, [query]);

  return (
    <section id="brands" data-testid="brands-showcase" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <SectionHeading
            label="Brands"
            title={`${ALL_BRANDS.length}+ maisons, ateliers & manufactures.`}
            sub="From heritage houses to contemporary icons — if it carries a name worth counterfeiting, we know how to verify it."
          />
          <FadeIn delay={0.1}>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  data-testid="brands-open-dialog-button"
                  variant="outline"
                  className="rounded-xl bg-transparent border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] hover:bg-[rgba(244,239,230,0.05)] hover:text-[color:var(--laa-ivory)] shrink-0"
                >
                  <Search size={15} className="mr-2 text-[color:var(--laa-champagne)]" />
                  Search all brands
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[color:var(--laa-ink-2)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-xl font-normal">Covered Brands</DialogTitle>
                </DialogHeader>
                <Input
                  data-testid="brands-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search brands…"
                  className="bg-[rgba(244,239,230,0.04)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] placeholder:text-[rgba(244,239,230,0.35)] focus-visible:ring-[color:var(--laa-champagne)] rounded-xl"
                />
                <ScrollArea className="h-[380px] pr-4">
                  {filtered.length === 0 ? (
                    <p data-testid="brands-no-results" className="text-sm text-[rgba(244,239,230,0.5)] py-8 text-center">
                      No brands match “{query}” — but we likely still cover it.{" "}
                      <a href="#contact" className="text-[color:var(--laa-champagne)] underline">Please enquire.</a>
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 py-2">
                      {filtered.map((b) => (
                        <p key={b} className="text-sm text-[rgba(244,239,230,0.75)] py-1 border-b border-[rgba(244,239,230,0.06)]">
                          {b}
                        </p>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </FadeIn>
        </div>

        {/* Featured marquee */}
        <FadeIn delay={0.12}>
          <div className="laa-marquee mt-12 overflow-hidden border-y border-[color:var(--laa-hairline)] py-5" data-testid="brands-marquee">
            <div className="laa-marquee-track">
              {[...FEATURED_BRANDS, ...FEATURED_BRANDS].map((b, i) => (
                <span key={`${b}-${i}`} className="font-serif-display text-xl sm:text-2xl text-[rgba(244,239,230,0.6)] whitespace-nowrap px-8 border-r border-[rgba(244,239,230,0.08)]">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </FadeIn>

        {/* Columns */}
        <FadeIn delay={0.15}>
          <div className="mt-12 columns-2 md:columns-4 gap-8" data-testid="brands-columns">
            {ALL_BRANDS.map((b) => (
              <p key={b} className="text-[13px] leading-7 text-[rgba(244,239,230,0.5)] break-inside-avoid hover:text-[rgba(244,239,230,0.85)]" style={{ transition: "color 180ms" }}>
                {b}
              </p>
            ))}
          </div>
          <p className="mt-10 text-sm text-[rgba(244,239,230,0.5)]">
            Something else?{" "}
            <a href="#contact" data-testid="brands-enquire-link" className="laa-link text-[color:var(--laa-champagne)]">
              Please enquire.
            </a>
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

/* ---------------- FOOTER ---------------- */

export const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-[color:var(--laa-hairline)] bg-[color:var(--laa-ink-2)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="font-serif-display text-xl text-[color:var(--laa-ivory)]">Luxury Appraisals & Authentication</p>
          <p className="mt-3 text-sm leading-relaxed text-[rgba(244,239,230,0.55)] max-w-sm">
            Founded in 2019 as a leading authority in resale — connoisseurship of collectible luxury goods,
            appraisals, authentication, and counterfeit detection.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="laa-label">Navigate</p>
          <div className="mt-4 flex flex-col gap-2.5">
            {[["#why", "Why Appraise"], ["#services", "Authentication"], ["#protection", "Brand Protection"], ["#request", "Request an Appraisal"], ["#about", "About"], ["#contact", "Contact"]].map(([href, label]) => (
              <a key={href} href={href} className="laa-link w-fit text-sm text-[rgba(244,239,230,0.6)] hover:text-[color:var(--laa-ivory)]">
                {label}
              </a>
            ))}
            <Link to="/design-options" className="laa-link w-fit text-sm text-[color:var(--laa-champagne)]">
              Design Options
            </Link>
          </div>
        </div>
        <div className="md:col-span-4">
          <a
            href={FASHION_DETECTIVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-fashion-detective-promo"
            className="laa-card block rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(244,239,230,0.03)] p-6"
          >
            <p className="laa-label">Our Sister Service</p>
            <p className="mt-2 font-serif-display text-lg text-[color:var(--laa-ivory)] flex items-center gap-2">
              The Fashion Detective <ArrowUpRight size={16} className="text-[color:var(--laa-champagne)]" />
            </p>
            <p className="mt-2 text-xs leading-relaxed text-[rgba(244,239,230,0.55)]">
              Authenticate your items now — fast, digital authentication for luxury goods.
            </p>
          </a>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-[color:var(--laa-hairline)] flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[rgba(244,239,230,0.4)]">© {new Date().getFullYear()} Luxury-Appraisals.com. All rights reserved.</p>
        <p className="text-xs text-[rgba(244,239,230,0.4)]">USPAP compliant · AAA certified · Expert witness services</p>
      </div>
    </div>
  </footer>
);
